import React, { useContext, useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { TError, TFetchStatus } from 'models';
import { exchangeTrustAuthCode, getQueryParam, isAllowedRedirectUrl, translate } from '../utils';
import { getTranslations } from '../config/translation';
import renderErrors from './AuthInsly3/common/Errors';
import { ErrorWrapper, Loader, LoadingScreen } from './styles';
import { AccountContext } from './Accounts';
import apiConfig from '../config/api';

const getDefaultErrors = (): TError[] => [{ code: '', message: translate({ key: 'sso.errors.default' }) }];

// Exchange errors use own translation namespace, backend codes (e.g. generic IDS99999) are
// already translated under sso.errors.* with unrelated login texts. Fall back to backend message.
const getExchangeErrors = (errors: TError[]): TError[] => errors.map(({ code, message }) => {
  const key = `sso.exchange.errors.${code}`;
  const translated = code ? translate({ key }) : key;

  return { code: '', message: translated !== key ? translated : message || translate({ key: 'sso.errors.default' }) };
});

const redirectTo = (url: string) => {
  try {
    (window.top || window).location.href = url;
  } catch (e) {
    // Top window navigation can be blocked when page is embedded cross-origin
    window.location.href = url;
  }
};

const TrustAuthExchange: React.FC = () => {
  // null - no error, empty array - generic error
  const [errors, setErrors] = useState<TError[] | null>(null);
  const [translationsStatus, setTranslationsStatus] = useState<TFetchStatus>(null);
  const { completeSSOLogin } = useContext(AccountContext);
  // Code is one-time, guard against double effect run (React StrictMode)
  const isExchangeStarted = useRef(false);

  useEffect(() => {
    if (isExchangeStarted.current) {
      return;
    }

    isExchangeStarted.current = true;

    // Translations are needed only for error messages, so exchange doesn't wait for them
    getTranslations({
      updateNetworkStatus: setTranslationsStatus,
      onError: () => {},
    });

    const code = getQueryParam('code');

    if (!code) {
      setErrors([]);
      return;
    }

    exchangeTrustAuthCode(code)
      .then(data => {
        if (!data?.authentication_result?.access_token) {
          setErrors([]);
          return;
        }

        completeSSOLogin(data.authentication_result);

        const redirectUrl = isAllowedRedirectUrl(data.redirect_url) ? data.redirect_url : apiConfig.QMT_APP_URL;

        redirectTo(redirectUrl);
      })
      .catch((error: AxiosError<{ errors?: TError[] }>) => {
        // Log only metadata, request config contains one-time code
        console.error('Trust auth exchange error:', error.response?.status, error.message);

        const responseErrors = error.response?.data?.errors;

        setErrors(responseErrors?.length ? responseErrors : []);
      });
  }, []);

  return (
    <LoadingScreen>
      {errors && translationsStatus !== 'loading' ? (
        <ErrorWrapper>
          {renderErrors(errors.length ? getExchangeErrors(errors) : getDefaultErrors())}
        </ErrorWrapper>
      ) : (
        <Loader />
      )}
    </LoadingScreen>
  );
};

export default TrustAuthExchange;
